import React, { useState, useEffect } from "react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { MapContainer } from "./place/MapContainer";
import { LocationConsiderations } from "./place/LocationConsiderations";
import { AILocationAnalysis } from "./place/AILocationAnalysis";
import { savePlaceData, loadPlaceData, LocationConsiderations as LocationConsiderationsType } from "./place/placeService";

interface PlaceTabProps {
  address: string;
  setAddress: (value: string) => void;
  shouldAnalyze: boolean;
  setShouldAnalyze: (value: boolean) => void;
  searchTrigger: number;
  setSearchTrigger: (value: number) => void;
  parties: Array<{
    type: string;
    name: string;
    address: string;
  }>;
  setParties: React.Dispatch<React.SetStateAction<Array<{
    type: string;
    name: string;
    address: string;
  }>>>;
  isAddPartyOpen: boolean;
  setIsAddPartyOpen: (value: boolean) => void;
  calculateEmptyRows: (items: any[], minRows?: number) => any[];
  aiEngine: string;
  setAiEngine: (value: string) => void;
}

export const PlaceTab: React.FC<PlaceTabProps> = ({
  address,
  setAddress,
  shouldAnalyze,
  setShouldAnalyze,
  searchTrigger,
  setSearchTrigger,
  parties,
  setParties,
  isAddPartyOpen,
  setIsAddPartyOpen,
  calculateEmptyRows,
  aiEngine,
  setAiEngine
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [defaultLocation, setDefaultLocation] = useState<google.maps.LatLngLiteral>({
    lat: 40,
    lng: -74.5
  });
  const [locationConsiderations, setLocationConsiderations] = useState<LocationConsiderationsType>({
    economic: 'Loading...',
    demographic: 'Loading...',
    cultural: 'Loading...',
    political: 'Loading...',
    market: 'Loading...',
    technological: 'Loading...',
    barriers: 'Loading...'
  });
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [placeId, setPlaceId] = useState<string | null>(null);
  const [isLoadingPlace, setIsLoadingPlace] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [selectedParty, setSelectedParty] = useState<{
    type: string;
    name: string;
    address: string;
  } | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [showNetworkAlert, setShowNetworkAlert] = useState(false);

  const onLocationAnalysis = (analysis: { accessibility: string; footTraffic: string; demographics: string }) => {
    console.log('Location analysis:', analysis);
  };

  const analyzeLocationWithAI = async (address: string, retryCount = 0) => {
    setIsAnalyzing(true);
    try {
      const prompt = `Analyze the following location for business considerations: ${address}. 
      Please provide a concise analysis for each of the following aspects. Format your response as exactly 7 numbered lines, each starting with the category name followed by a colon:
      0. Economic Environment: (Income Levels and Economic Stability)
      1. Demographic Factors: (Population Size and Growth, Age Distribution, Education Levels)
      2. Cultural and Social Factors: (Language, Cultural Norms, Consumer Behaviour)
      3. Political and Legal Environment: (Regulatory Framework, Political Stability, Trade Policies)
      4. Market Dynamics: (Competition, Market Demand, Distribution Channels)
      5. Technological Environment: (Infrastructure, Digital Adoption)
      6. Barriers to Entry: (Market Access, Local Partnerships)
      
      IMPORTANT: Return exactly 7 lines, one per category. Each line must start with the category name followed by a colon and then the analysis.`;

      const messages = [{
        role: 'system',
        content: 'You are a business location analyst. Return exactly 7 lines of analysis, one per category. Each line starts with the category name followed by a colon and the analysis text. Do not include any headers, titles, or extra formatting.'
      }, {
        role: 'user',
        content: prompt
      }];

      try {
        const { data: aiData, error: aiError } = await supabase.functions.invoke('ai-chat-completion', {
          body: { 
            messages, 
            model: 'gpt-4o-mini' 
          }
        });

        if (aiError) {
          console.error('Error calling ai-chat-completion:', aiError);
          setShowNetworkAlert(true);
          return;
        }

        const analysisContent = aiData?.choices?.[0]?.message?.content;
        
        if (!analysisContent) {
          console.error('No content in AI response');
          setShowNetworkAlert(true);
          return;
        }

        const lines = analysisContent.split('\n').filter((line: string) => line.trim()).filter((line: string) => !line.includes('Business Location Analysis'));
        
        // More robust parsing - find lines containing category keywords
        const findLine = (keywords: string[]) => {
          const found = lines.find((line: string) => keywords.some(kw => line.toLowerCase().includes(kw.toLowerCase())));
          if (found) {
            const colonIdx = found.indexOf(':');
            return colonIdx !== -1 ? found.substring(colonIdx + 1).trim().replace(/^\*+|\*+$/g, '').trim() : found.trim();
          }
          return 'Analysis unavailable';
        };

        const considerations: LocationConsiderationsType = {
          economic: findLine(['Economic Environment', 'Economic', 'Income Level']),
          demographic: findLine(['Demographic', 'Population']),
          cultural: findLine(['Cultural', 'Social Factor']),
          political: findLine(['Political', 'Legal Environment']),
          market: findLine(['Market Dynamics', 'Market Demand', 'Competition']),
          technological: findLine(['Technological', 'Technology', 'Digital']),
          barriers: findLine(['Barriers', 'Market Access']),
        };

        // Check if Economic Environment needs retry
        if ((considerations.economic === '**' || considerations.economic === 'Analysis unavailable' || !considerations.economic) && retryCount < 3) {
          console.log(`Economic Environment analysis inadequate, retrying... (attempt ${retryCount + 1})`);
          setTimeout(() => {
            analyzeLocationWithAI(address, retryCount + 1);
          }, 1000);
          return;
        }

        const hasValidData = Object.values(considerations).some(value => value && value !== 'Analysis unavailable' && value !== 'Loading...' && value !== '**');

        if (!hasValidData) {
          setShowNetworkAlert(true);
        } else {
          setLocationConsiderations(considerations);
          await savePlaceData(address, considerations, placeId, setPlaceId);
        }
      } catch (error) {
        console.error('Error analyzing location:', error);
        setShowNetworkAlert(true);
      }
    } catch (error) {
      console.error('Error preparing location analysis:', error);
      setShowNetworkAlert(true);
    } finally {
      setIsAnalyzing(false);
    }
  };

  useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        position => {
          setDefaultLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        error => {
          console.error('Error getting location:', error);
          toast.error('Could not get your location. Using default location.');
        }
      );
    }
  }, []);

  useEffect(() => {
    loadPlaceData(setPlaceId, setAddress, setLocationConsiderations, setDataLoaded, setIsLoadingPlace);
  }, []);

  const handleSearch = () => {
    console.log("Searching for:", address);
    setShouldAnalyze(true);
    setSearchTrigger(searchTrigger + 1);

    if (address) {
      analyzeLocationWithAI(address);
    }
  };

  const handleMarkerClick = (party: { type: string; name: string; address: string }) => {
    setSelectedParty(party);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteParty = () => {
    if (selectedParty) {
      const updatedParties = parties.filter(party => 
        !(party.type === selectedParty.type && 
          party.name === selectedParty.name && 
          party.address === selectedParty.address)
      );
      setParties(updatedParties);
      setIsDeleteDialogOpen(false);
      setSelectedParty(null);
      toast.success('Location pin deleted successfully');
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <p className="text-[12px] text-secondary-light">
        Use AI to determine the location analysis, including considerations for economic, demographic, cultural, social, political, legal, market and technology factors, as well as barriers of entry.
      </p>
      
      <div className="flex gap-2">
        <AILocationAnalysis
          address={address}
          setAddress={setAddress}
          aiEngine={aiEngine}
          setAiEngine={setAiEngine}
          onSearch={handleSearch}
          setShouldAnalyze={setShouldAnalyze}
        >
          <MapContainer
            defaultLocation={defaultLocation}
            address={address}
            shouldAnalyze={shouldAnalyze}
            searchTrigger={searchTrigger}
            parties={parties}
            onLocationAnalysis={onLocationAnalysis}
            onMarkerClick={handleMarkerClick}
            isLoaded={isLoaded}
            setIsLoaded={setIsLoaded}
          />
        </AILocationAnalysis>

        <LocationConsiderations
          considerations={locationConsiderations}
          aiEngine={aiEngine}
          isAnalyzing={isAnalyzing}
          isLoadingPlace={isLoadingPlace}
        />
      </div>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Location Pin</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this location pin? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setIsDeleteDialogOpen(false);
              setSelectedParty(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteParty}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showNetworkAlert} onOpenChange={setShowNetworkAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Network Issue</AlertDialogTitle>
            <AlertDialogDescription>
              Network conditions are affecting the query. Please try again, or try using a different AI engine.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowNetworkAlert(false)}>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
